import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import ModuleLayout from '../components/ModuleLayout'
import Quiz from '../components/Quiz'
import { Search, CheckCircle2, Play, SkipForward } from 'lucide-react'

/* ─── Quiz Data ─── */
const QUIZ = [
  {
    question: 'What does VLOOKUP stand for?',
    options: [
      'Variable Lookup',
      'Vertical Lookup — it searches down a column',
      'Value Lookup — it finds values',
      'Visual Lookup — it highlights matches',
    ],
    correctIndex: 1,
    explanation:
      'VLOOKUP stands for Vertical Lookup. It searches vertically (downward) through the first column of a range to find a match.',
  },
  {
    question:
      'In =VLOOKUP("offer_123", A:C, 3, FALSE), what does the 3 mean?',
    context: {
      headers: ['A: Offer ID', 'B: Name', 'C: Clicks'],
      rows: [
        ['offer_101', 'CreditCards Plus', '2,340'],
        ['offer_123', 'InsureMe Now', '1,250'],
        ['offer_145', 'TravelDeals Pro', '3,100'],
      ],
    },
    options: [
      'Search the 3rd row',
      'Return the value from the 3rd column of the range (Clicks)',
      'The search key is in row 3',
      'Look at 3 columns total',
    ],
    correctIndex: 1,
    explanation:
      'The third argument (column_index) tells VLOOKUP which column of the range to return the value from. Here, 3 means "give me the value from column C" (the 3rd column in the A:C range), which is 1,250.',
  },
  {
    question:
      'Why should you (almost) always use FALSE as the last argument?',
    options: [
      'FALSE is faster',
      'TRUE is deprecated',
      'FALSE means exact match — TRUE can return incorrect approximate matches',
      'FALSE searches the entire column, TRUE only searches part',
    ],
    correctIndex: 2,
    explanation:
      'FALSE tells VLOOKUP to find an exact match. TRUE uses approximate matching (binary search), which requires sorted data and can silently return wrong results if the data is not sorted. Almost always use FALSE.',
  },
  {
    question: 'VLOOKUP searches in which direction?',
    options: [
      'Left to right across a row',
      'Down (vertically) through the first column of the range',
      'In all directions',
      'Up from the bottom of the range',
    ],
    correctIndex: 1,
    explanation:
      'VLOOKUP searches downward through the FIRST column of the specified range. That is the "V" (vertical) in VLOOKUP.',
  },
  {
    question: 'What happens if VLOOKUP can\'t find the search key?',
    options: [
      'It returns 0',
      'It returns blank',
      'It returns a #N/A error',
      'It returns the closest match',
    ],
    correctIndex: 2,
    explanation:
      'When VLOOKUP cannot find the search key (with FALSE/exact match), it returns a #N/A error. You can handle this by wrapping the formula in IFERROR.',
  },
  {
    question: 'How do you handle a VLOOKUP that might error?',
    options: [
      'Use TRUE instead of FALSE',
      'Add a second VLOOKUP',
      'Wrap it in IFERROR, e.g., =IFERROR(VLOOKUP(...), "Not found")',
      'There is no way to handle it',
    ],
    correctIndex: 2,
    explanation:
      'Wrapping VLOOKUP in IFERROR catches the #N/A error and shows a fallback value instead. For example: =IFERROR(VLOOKUP("offer_999", A:C, 3, FALSE), "Not found").',
  },
  {
    question: 'In =VLOOKUP(E2, A:D, 2, FALSE), which column does VLOOKUP search?',
    options: [
      'Column E',
      'Column A — the first column in the range',
      'Column B — the second column in the range',
      'Column D — the last column in the range',
    ],
    correctIndex: 1,
    explanation:
      'VLOOKUP always searches in the FIRST column of the specified range. Since the range is A:D, it searches column A for the value in E2.',
  },
  {
    question: 'What is the column_index for the first column in the range?',
    options: [
      '0',
      '1',
      '2',
      'It depends on the column letter',
    ],
    correctIndex: 1,
    explanation:
      'The column_index starts at 1. Column 1 is the search column itself, column 2 is the next column to the right, and so on. The index is relative to the range, not the sheet.',
  },
  {
    question: 'You typed =VLOOKUP("offer_123", A:C, 2, FALSE) but wanted Clicks from column C. What went wrong?',
    context: {
      headers: ['A: Offer ID', 'B: Name', 'C: Clicks'],
      rows: [
        ['offer_101', 'CreditCards Plus', '2,340'],
        ['offer_123', 'InsureMe Now', '1,250'],
        ['offer_145', 'TravelDeals Pro', '3,100'],
      ],
    },
    options: [
      'The search key is wrong',
      'You used the wrong column_index -- column C is 3, not 2',
      'FALSE should be TRUE',
      'The range should be A:B instead',
    ],
    correctIndex: 1,
    explanation:
      'In the range A:C, column A = 1, column B = 2, column C = 3. Using 2 returns "InsureMe Now" (column B) instead of 1,250 (column C). Change the column_index to 3.',
  },
  {
    question: 'Can VLOOKUP return a value from a column to the LEFT of the search column?',
    options: [
      'Yes, use a negative column_index',
      'Yes, use TRUE as the last argument',
      'No — VLOOKUP can only return values to the right of the search column',
      'Yes, just reverse the range',
    ],
    correctIndex: 2,
    explanation:
      'VLOOKUP can only return values from columns to the right of (or the same as) the search column. This is one of its main limitations. INDEX/MATCH can look in any direction.',
  },
  {
    question: 'What does =VLOOKUP("offer_123", A:C, 3, TRUE) do if column A is not sorted?',
    options: [
      'It finds the exact match anyway',
      'It returns an error',
      'It may return a wrong result without any warning',
      'It automatically sorts the data first',
    ],
    correctIndex: 2,
    explanation:
      'TRUE uses approximate matching which assumes column A is sorted. If the data is not sorted, VLOOKUP can silently return a completely wrong value. This is why you should almost always use FALSE.',
  },
  {
    question: 'What does =IFERROR(VLOOKUP("offer_999", A:C, 3, FALSE), "Not found") return?',
    context: {
      headers: ['A: Offer ID', 'B: Name', 'C: Clicks'],
      rows: [
        ['offer_101', 'CreditCards Plus', '2,340'],
        ['offer_123', 'InsureMe Now', '1,250'],
        ['offer_145', 'TravelDeals Pro', '3,100'],
      ],
    },
    options: [
      '#N/A',
      '0',
      'Not found',
      'An empty cell',
    ],
    correctIndex: 2,
    explanation:
      'Since offer_999 does not exist in the table, VLOOKUP returns a #N/A error. IFERROR catches that error and displays the fallback text "Not found" instead.',
  },
]

/* ─── Section card wrapper ─── */
function Section({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay, duration: 0.5, ease: 'easeOut' }}
      className="rounded-xl border border-slate-200 bg-white p-8"
    >
      {children}
    </motion.div>
  )
}

/* ─── Lookup data used throughout ─── */
const LOOKUP_TABLE = [
  { id: 'offer_101', name: 'CreditCards Plus', clicks: 2340 },
  { id: 'offer_102', name: 'HomeLoans Direct', clicks: 1890 },
  { id: 'offer_123', name: 'InsureMe Now', clicks: 1250 },
  { id: 'offer_145', name: 'TravelDeals Pro', clicks: 3100 },
  { id: 'offer_167', name: 'SaveMore Bank', clicks: 890 },
  { id: 'offer_200', name: 'QuickLoans', clicks: 1560 },
]

/* ─── VLOOKUP Animated Walkthrough ─── */
function VLookupAnimation() {
  const [step, setStep] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [autoAdvance, setAutoAdvance] = useState(false)

  const searchKey = 'offer_123'
  const matchIndex = LOOKUP_TABLE.findIndex((r) => r.id === searchKey)
  const matchResult = LOOKUP_TABLE[matchIndex].clicks

  const STEPS = [
    {
      label: 'The formula',
      description: 'We want to find the clicks for offer_123',
      formulaHighlight: 'full',
    },
    {
      label: 'Search key',
      description: `VLOOKUP takes the search key "${searchKey}" and starts looking...`,
      formulaHighlight: 'search_key',
    },
    {
      label: 'Scanning column A',
      description: 'It walks down column A, checking each cell...',
      formulaHighlight: 'range',
      scanRow: 0,
    },
    {
      label: 'Scanning...',
      description: `Checking: "${LOOKUP_TABLE[1].id}" — not a match`,
      formulaHighlight: 'range',
      scanRow: 1,
    },
    {
      label: 'Match found!',
      description: `Found "${searchKey}" in row ${matchIndex + 2}!`,
      formulaHighlight: 'range',
      scanRow: matchIndex,
      matched: true,
    },
    {
      label: 'Moving to column 3',
      description: 'Column index is 3, so we slide right to the Clicks column...',
      formulaHighlight: 'column_index',
      scanRow: matchIndex,
      matched: true,
      slideToCol: true,
    },
    {
      label: 'Result!',
      description: `The answer is ${matchResult.toLocaleString()} clicks`,
      formulaHighlight: 'result',
      scanRow: matchIndex,
      matched: true,
      slideToCol: true,
      showResult: true,
    },
  ]

  const totalSteps = STEPS.length
  const currentStep = STEPS[step]

  useEffect(() => {
    if (!autoAdvance || !isPlaying) return
    if (step >= totalSteps - 1) {
      const t = setTimeout(() => setIsPlaying(false), 0)
      return () => clearTimeout(t)
    }
    const timer = setTimeout(() => {
      setStep((s) => s + 1)
    }, 1800)
    return () => clearTimeout(timer)
  }, [step, autoAdvance, isPlaying, totalSteps])

  const handlePlay = () => {
    setStep(0)
    setAutoAdvance(true)
    setIsPlaying(true)
  }

  const handleNextStep = () => {
    setAutoAdvance(false)
    setIsPlaying(false)
    if (step < totalSteps - 1) setStep((s) => s + 1)
    else setStep(0)
  }

  const formulaHighlightColors = {
    search_key: 'text-blue-400 bg-blue-900/50',
    range: 'text-green-400 bg-green-900/50',
    column_index: 'text-purple-400 bg-purple-900/50',
    is_sorted: 'text-amber-400 bg-amber-900/50',
  }

  function renderFormula() {
    if (currentStep.formulaHighlight === 'full' || currentStep.formulaHighlight === 'result') {
      return (
        <span className="text-emerald-400">
          =VLOOKUP("offer_123", A:C, 3, FALSE)
        </span>
      )
    }

    const parts = [
      { text: '=VLOOKUP(', key: null },
      { text: '"offer_123"', key: 'search_key' },
      { text: ', ', key: null },
      { text: 'A:C', key: 'range' },
      { text: ', ', key: null },
      { text: '3', key: 'column_index' },
      { text: ', ', key: null },
      { text: 'FALSE', key: 'is_sorted' },
      { text: ')', key: null },
    ]

    return parts.map((p, i) => {
      const isHighlighted = p.key === currentStep.formulaHighlight
      return (
        <span
          key={i}
          className={
            isHighlighted
              ? `${formulaHighlightColors[p.key]} rounded px-1 font-bold`
              : p.key
              ? 'text-gray-400'
              : 'text-gray-500'
          }
        >
          {p.text}
        </span>
      )
    })
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center gap-3">
        <button
          onClick={handlePlay}
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700"
        >
          <Play className="w-4 h-4" />
          {step > 0 ? 'Replay' : 'Play Animation'}
        </button>
        <button
          onClick={handleNextStep}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
        >
          <SkipForward className="w-4 h-4" />
          {step >= totalSteps - 1 ? 'Reset' : 'Next Step'}
        </button>
        <span className="text-xs text-gray-400">
          Step {step + 1} of {totalSteps}
        </span>
      </div>

      {/* Step progress */}
      <div className="flex gap-1">
        {STEPS.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
              i <= step ? 'bg-emerald-500' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>

      {/* Formula bar */}
      <div className="rounded-lg bg-gray-900 p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-gray-500 font-mono">fx</span>
          <div className="w-px h-3 bg-gray-700" />
        </div>
        <pre className="font-mono text-base">
          {renderFormula()}
        </pre>
      </div>

      {/* Step description */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ duration: 0.2 }}
          className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3"
        >
          <p className="text-sm font-semibold text-blue-800">{currentStep.label}</p>
          <p className="text-sm text-blue-700">{currentStep.description}</p>
        </motion.div>
      </AnimatePresence>

      {/* Animated table */}
      <div className="overflow-hidden rounded-lg border border-gray-300 shadow-sm">
        {/* Result bar - shows when result is found */}
        <AnimatePresence>
          {currentStep.showResult && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center justify-center gap-2 bg-emerald-500 px-4 py-2.5 text-white"
            >
              <CheckCircle2 className="w-5 h-5" />
              <span className="font-semibold">
                Result: {matchResult.toLocaleString()} clicks
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="border-b border-r border-gray-300 px-3 py-2 text-center text-xs text-gray-500 w-10" />
              <th className="border-b border-r border-gray-300 px-3 py-2 text-center text-xs font-medium text-gray-600 min-w-[120px]">
                A (Offer ID)
              </th>
              <th className="border-b border-r border-gray-300 px-3 py-2 text-center text-xs font-medium text-gray-600 min-w-[120px]">
                B (Name)
              </th>
              <th className="border-b border-gray-300 px-3 py-2 text-center text-xs font-medium text-gray-600 min-w-[80px]">
                C (Clicks)
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Header row */}
            <tr>
              <td className="bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 py-2">
                1
              </td>
              <td className="border-b border-r border-gray-200 px-3 py-2 font-semibold text-gray-700 bg-gray-50">
                Offer ID
              </td>
              <td className="border-b border-r border-gray-200 px-3 py-2 font-semibold text-gray-700 bg-gray-50">
                Name
              </td>
              <td className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-700 bg-gray-50">
                Clicks
              </td>
            </tr>

            {LOOKUP_TABLE.map((row, i) => {
              const isScanning =
                currentStep.scanRow !== undefined && i <= currentStep.scanRow
              const isCurrentScan =
                currentStep.scanRow !== undefined && i === currentStep.scanRow
              const isMatch = isCurrentScan && currentStep.matched
              const showSlide = isMatch && currentStep.slideToCol

              // Column A highlight
              let colAClass = 'border-b border-r border-gray-200 px-3 py-2 text-gray-800'
              if (isMatch) {
                colAClass =
                  'border-b border-r border-gray-200 px-3 py-2 bg-emerald-100 text-emerald-800 font-semibold'
              } else if (isCurrentScan) {
                colAClass =
                  'border-b border-r border-gray-200 px-3 py-2 bg-yellow-100 text-yellow-800'
              } else if (isScanning) {
                colAClass =
                  'border-b border-r border-gray-200 px-3 py-2 bg-gray-100 text-gray-400'
              }

              // Column B
              let colBClass =
                'border-b border-r border-gray-200 px-3 py-2 text-gray-600'
              if (isMatch && showSlide) {
                colBClass =
                  'border-b border-r border-gray-200 px-3 py-2 bg-emerald-50 text-emerald-700'
              }

              // Column C
              let colCClass = 'border-b border-gray-200 px-3 py-2 text-gray-800'
              if (isMatch && showSlide) {
                colCClass =
                  'border-b border-gray-200 px-3 py-2 bg-emerald-200 text-emerald-900 font-bold'
              }

              return (
                <motion.tr
                  key={row.id}
                  animate={{
                    backgroundColor:
                      isMatch
                        ? 'rgba(209, 250, 229, 0.3)'
                        : 'rgba(255,255,255,1)',
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <td className="bg-gray-100 border-b border-r border-gray-300 text-center text-xs text-gray-500 py-2">
                    {i + 2}
                  </td>
                  <td className={`transition-colors duration-300 ${colAClass}`}>
                    <div className="flex items-center gap-2">
                      {isCurrentScan && !currentStep.matched && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-1.5 h-1.5 rounded-full bg-yellow-500"
                        />
                      )}
                      {isMatch && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', stiffness: 500 }}
                        >
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        </motion.div>
                      )}
                      <span className="font-mono text-xs">{row.id}</span>
                    </div>
                  </td>
                  <td className={`transition-colors duration-300 ${colBClass}`}>
                    {row.name}
                    {isMatch && showSlide && (
                      <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.4 }}
                        className="h-0.5 bg-emerald-400 mt-1 origin-left rounded-full"
                      />
                    )}
                  </td>
                  <td className={`transition-colors duration-300 ${colCClass}`}>
                    {row.clicks.toLocaleString()}
                    {isMatch && showSlide && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, type: 'spring', stiffness: 400 }}
                        className="text-xs mt-1 text-emerald-600 font-medium"
                      >
                        This is the answer
                      </motion.div>
                    )}
                  </td>
                </motion.tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ─── Argument Breakdown ─── */
function ArgumentBreakdown() {
  const [activeArg, setActiveArg] = useState(null)

  const args = [
    {
      key: 'search_key',
      label: 'search_key',
      value: '"offer_123"',
      question: 'What are you looking for?',
      description:
        'The value you want to find in the first column of your range. This is usually a cell reference like E2, but can also be a text string in quotes.',
      color: 'blue',
    },
    {
      key: 'range',
      label: 'range',
      value: 'A:C',
      question: 'Where should I look?',
      description:
        'The table to search in. VLOOKUP always looks in the FIRST column of this range for the search key, then returns a value from another column in the same range.',
      color: 'green',
    },
    {
      key: 'column_index',
      label: 'column_index',
      value: '3',
      question: 'Which column has the answer?',
      description:
        'A number indicating which column of the range to return. 1 = first column (the search column itself), 2 = second column, 3 = third column, etc.',
      color: 'purple',
    },
    {
      key: 'is_sorted',
      label: 'is_sorted',
      value: 'FALSE',
      question: 'Is the first column sorted?',
      description:
        'Almost always use FALSE for exact match. TRUE assumes the first column is sorted and uses approximate matching, which can return wrong results.',
      color: 'amber',
    },
  ]

  const colorMap = {
    blue: {
      active: 'border-blue-400 bg-blue-50 text-blue-800',
      badge: 'bg-blue-100 text-blue-700',
    },
    green: {
      active: 'border-green-400 bg-green-50 text-green-800',
      badge: 'bg-green-100 text-green-700',
    },
    purple: {
      active: 'border-purple-400 bg-purple-50 text-purple-800',
      badge: 'bg-purple-100 text-purple-700',
    },
    amber: {
      active: 'border-amber-400 bg-amber-50 text-amber-800',
      badge: 'bg-amber-100 text-amber-700',
    },
  }

  return (
    <div className="space-y-4">
      {/* Formula with clickable parts */}
      <div className="rounded-lg bg-gray-900 p-4">
        <p className="text-xs text-gray-500 mb-2 font-mono">Click each argument to learn more:</p>
        <pre className="font-mono text-base flex flex-wrap items-center gap-0">
          <span className="text-gray-500">=VLOOKUP(</span>
          {args.map((arg, i) => (
            <span key={arg.key} className="inline-flex items-center">
              <button
                onClick={() => setActiveArg(activeArg === arg.key ? null : arg.key)}
                className={`rounded px-1.5 py-0.5 transition-all duration-150 cursor-pointer ${
                  activeArg === arg.key
                    ? `${colorMap[arg.color].badge} font-bold ring-2 ring-offset-1 ring-offset-gray-900 ring-${arg.color}-400`
                    : 'text-gray-300 hover:text-white hover:bg-gray-800'
                }`}
              >
                {arg.value}
              </button>
              {i < args.length - 1 && <span className="text-gray-500">, </span>}
            </span>
          ))}
          <span className="text-gray-500">)</span>
        </pre>
      </div>

      {/* Argument cards */}
      <div className="grid gap-3 sm:grid-cols-2">
        {args.map((arg) => {
          const isActive = activeArg === arg.key
          const colors = colorMap[arg.color]
          return (
            <motion.button
              key={arg.key}
              onClick={() => setActiveArg(isActive ? null : arg.key)}
              className={`rounded-lg border-2 p-4 text-left transition-all ${
                isActive ? colors.active : 'border-gray-200 bg-gray-50 hover:border-gray-300'
              }`}
              layout
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`text-xs font-bold px-2 py-0.5 rounded ${colors.badge}`}
                >
                  {arg.label}
                </span>
                <code className="text-xs font-mono text-gray-600">{arg.value}</code>
              </div>
              <p className="text-sm font-medium text-gray-800 mb-1">{arg.question}</p>
              <AnimatePresence>
                {isActive && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-xs text-gray-600 mt-2 leading-relaxed"
                  >
                    {arg.description}
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}

/* ─── Common Mistakes ─── */
function CommonMistakes() {
  const mistakes = [
    {
      title: 'Wrong column index',
      formula: '=VLOOKUP("offer_123", A:C, 2, FALSE)',
      problem: 'You wanted Clicks (column C = 3), but you typed 2.',
      result: '"InsureMe Now"',
      resultLabel: 'Name (column 2), not Clicks (column 3)',
      fix: 'Count the columns in your range: A=1, B=2, C=3. The column index is relative to the range, not the whole sheet.',
      icon: '🔢',
      color: 'border-amber-200 bg-amber-50',
    },
    {
      title: 'TRUE instead of FALSE',
      formula: '=VLOOKUP("offer_150", A:C, 3, TRUE)',
      problem:
        'TRUE means approximate match. If the data is not perfectly sorted, VLOOKUP may return a completely wrong row.',
      result: '3,100 (from offer_145)',
      resultLabel: 'Wrong offer! Approximate match returned the closest ID it could find.',
      fix: 'Always use FALSE unless you specifically need approximate matching (rare).',
      icon: '⚠️',
      color: 'border-red-200 bg-red-50',
    },
    {
      title: '#N/A error',
      formula: '=VLOOKUP("offer_999", A:C, 3, FALSE)',
      problem: '"offer_999" does not exist in column A.',
      result: '#N/A',
      resultLabel: 'Error! The search key was not found.',
      fix: 'Wrap in IFERROR: =IFERROR(VLOOKUP("offer_999", A:C, 3, FALSE), "Not found")',
      icon: '❌',
      color: 'border-red-200 bg-red-50',
    },
  ]

  return (
    <div className="space-y-4">
      {mistakes.map((m, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.15, duration: 0.4 }}
          className={`rounded-xl border-2 p-6 ${m.color}`}
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl">{m.icon}</span>
            <div className="flex-1 space-y-3">
              <h4 className="font-bold text-gray-800">{m.title}</h4>
              <div className="rounded-lg bg-gray-900 px-3 py-2">
                <code className="text-sm text-red-400 font-mono">{m.formula}</code>
              </div>
              <p className="text-sm text-gray-700">{m.problem}</p>
              <div className="flex items-center gap-2 text-sm">
                <span className="font-mono font-bold text-red-700 bg-red-100 rounded px-2 py-0.5">
                  {m.result}
                </span>
                <span className="text-gray-500">{m.resultLabel}</span>
              </div>
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                <p className="text-sm text-emerald-800">
                  <strong>Fix:</strong> {m.fix}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  )
}

/* ─── Main Module ─── */
export default function Module5() {
  return (
    <ModuleLayout moduleId={5}>
      <div className="space-y-8">
        {/* Section 1: The Problem */}
        <Section delay={0}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">The Problem</h2>
              <p className="text-lg text-gray-600">
                You have a table of click data. You know the Offer ID. You want to find the clicks
                for that specific offer.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              {/* The search value */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="flex flex-col items-center justify-center rounded-xl border-2 border-blue-300 bg-blue-50 p-6"
              >
                <Search className="w-8 h-8 text-blue-500 mb-2" />
                <p className="text-sm text-blue-600 mb-2">You have:</p>
                <div className="rounded-lg bg-white border border-blue-200 px-4 py-2 font-mono font-bold text-blue-800">
                  offer_123
                </div>
              </motion.div>

              {/* The table */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="overflow-hidden rounded-lg border border-gray-300 shadow-sm"
              >
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border-b border-r border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-600">
                        Offer ID
                      </th>
                      <th className="border-b border-gray-300 px-3 py-2 text-left text-xs font-medium text-gray-600">
                        Clicks
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {LOOKUP_TABLE.slice(0, 4).map((row, i) => (
                      <tr key={i}>
                        <td className="border-b border-r border-gray-200 px-3 py-1.5 font-mono text-xs text-gray-700">
                          {row.id}
                        </td>
                        <td className="border-b border-gray-200 px-3 py-1.5 text-gray-700">
                          {row.clicks.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td
                        colSpan={2}
                        className="px-3 py-1.5 text-center text-xs text-gray-400 italic"
                      >
                        ... and hundreds more rows
                      </td>
                    </tr>
                  </tbody>
                </table>
              </motion.div>
            </div>

            <div className="space-y-3">
              <p className="text-gray-700">
                You could scroll through the table and find it manually. But what if there are{' '}
                <strong>1,000 rows</strong>?
              </p>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-center"
              >
                <p className="text-emerald-800 font-semibold text-lg">
                  VLOOKUP does this automatically.
                </p>
              </motion.div>
            </div>
          </div>
        </Section>

        {/* Section 2: VLOOKUP Explained */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">VLOOKUP Explained</h2>
              <p className="text-lg text-gray-600">
                VLOOKUP has four arguments. Click each one to understand what it does.
              </p>
            </div>

            <ArgumentBreakdown />

            <div className="border-t border-gray-200 pt-6">
              <h3 className="mb-4 text-lg font-semibold text-gray-800">
                Watch it work step by step
              </h3>
              <VLookupAnimation />
            </div>
          </div>
        </Section>

        {/* Section 3: Common Mistakes */}
        <Section delay={0.1}>
          <div className="space-y-6">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-gray-900">Common Mistakes</h2>
              <p className="text-lg text-gray-600">
                These three mistakes account for most VLOOKUP problems. Recognise them and you
                will save yourself hours of debugging.
              </p>
            </div>

            <CommonMistakes />
          </div>
        </Section>

        {/* Section 4: Quiz */}
        <Section delay={0.1}>
          <Quiz moduleId={5} questions={QUIZ} />
        </Section>
      </div>
    </ModuleLayout>
  )
}
